import React from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCalendarEvent,
  IconCircleCheck,
  IconClockHour4,
  IconFileText,
  IconInbox,
  IconPlayerPlay,
  IconPlus,
  IconRefresh,
} from "@tabler/icons-react";
import { useAuth } from "../../../auth/AuthContext";
import type { Meeting } from "../../../api/bidCommittee";
import { EmptyState, KpiTile, SectionCard, StatusBadge, tabular, type Kpi } from "../../../components/DashboardParts";
import { dhivehiDisplayStyle } from "../../../utils/dhivehiText";
import { RoleGuard } from "../components/RoleGuard";
import {
  useAwardSheets,
  useMeetings,
  useNeedsEndorsement,
  usePendingAgendaReturnFormApprovals,
  usePendingAwardSheetApprovals,
} from "../hooks";

const PENDING_APPROVALS_PATH = "/tenders/pending-approvals";

type PendingItem = {
  key: string;
  kind: string;
  reference: string;
  title: string;
  reason: string;
  to: string;
};

function signingReason(signingAs: string | undefined): string {
  if (signingAs === "CHAIR") return "Chairperson approval";
  if (signingAs === "SECRETARY") return "Secretary signature";
  return "Your vote";
}

/** Meetings still to come, the one in progress first, then by start time. */
function upcomingMeetings(meetings: Meeting[]): Meeting[] {
  const today = dayjs().startOf("day");
  return meetings
    .filter(
      (m) =>
        m.status === "IN_PROGRESS" ||
        ((m.status === "SCHEDULED" || m.status === "DRAFT") && !dayjs(m.plannedStart).isBefore(today))
    )
    .sort(
      (a, b) =>
        Number(b.status === "IN_PROGRESS") - Number(a.status === "IN_PROGRESS") ||
        dayjs(a.plannedStart).valueOf() - dayjs(b.plannedStart).valueOf()
    );
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

function BidDashboardSkeleton() {
  return (
    <Stack gap="lg" aria-busy="true" aria-label="Loading bid committee dashboard">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} h={126} radius="md" />
        ))}
      </SimpleGrid>
      <Grid>
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Skeleton h={280} radius="md" />
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Skeleton h={280} radius="md" />
        </Grid.Col>
      </Grid>
      <Skeleton h={320} radius="md" />
    </Stack>
  );
}

export const BidDashboard: React.FC = () => {
  const { user } = useAuth();
  const isCeo = user?.role === "CEO";
  const meetingsQuery = useMeetings();
  const awardSheetsQuery = useAwardSheets();
  const awardSheetApprovalsQuery = usePendingAwardSheetApprovals();
  const returnFormApprovalsQuery = usePendingAgendaReturnFormApprovals();
  const needsEndorsementQuery = useNeedsEndorsement(isCeo);

  const queries = [meetingsQuery, awardSheetsQuery, awardSheetApprovalsQuery, returnFormApprovalsQuery];
  const isFetching = queries.some((q) => q.isFetching) || needsEndorsementQuery.isFetching;
  const refetchAll = () => {
    queries.forEach((q) => q.refetch());
    if (isCeo) needsEndorsementQuery.refetch();
  };

  const meetings = meetingsQuery.data ?? [];
  const awardSheets = awardSheetsQuery.data ?? [];
  const upcoming = upcomingMeetings(meetings);
  const inProgress = upcoming.filter((m) => m.status === "IN_PROGRESS");
  const scheduled = upcoming.filter((m) => m.status !== "IN_PROGRESS");
  const latestMeetings = meetings.slice(0, 8);

  const awardSheetCountByMeeting = new Map<string, number>();
  for (const sheet of awardSheets) {
    awardSheetCountByMeeting.set(sheet.meetingId, (awardSheetCountByMeeting.get(sheet.meetingId) ?? 0) + 1);
  }
  const sheetsInSigning = awardSheets.filter((s) => s.status === "PENDING").length;
  const sheetsApproved = awardSheets.filter((s) => s.status === "APPROVED").length;
  const sheetsInDraft = awardSheets.filter((s) => s.status === "DRAFT").length;

  const pendingItems: PendingItem[] = [
    ...(awardSheetApprovalsQuery.data ?? []).map((a: any) => ({
      key: `award-sheet-${a.id}`,
      kind: "Award sheet",
      reference: a.awardSheet?.awardSheetNumber ?? "Award sheet",
      title: a.awardSheet?.tenderName || a.awardSheet?.vendor || a.awardSheet?.meeting?.title || "",
      reason: signingReason(a.signingAs),
      to: PENDING_APPROVALS_PATH,
    })),
    ...(returnFormApprovalsQuery.data ?? []).map((a) => ({
      key: `return-form-${a.id}`,
      kind: "Return form",
      reference: a.agendaReturnForm?.formNumber ?? "Return form",
      title: a.agendaReturnForm?.agendaItem?.subject ?? a.agendaReturnForm?.meeting?.title ?? "",
      reason: signingReason(a.signingAs),
      to: PENDING_APPROVALS_PATH,
    })),
    ...(isCeo ? needsEndorsementQuery.data?.data ?? [] : []).map((s: any) => ({
      key: `endorsement-${s.id}`,
      kind: "Award sheet",
      reference: s.awardSheetNumber ?? s.tenderNumber ?? "Award sheet",
      title: s.tenderName || s.vendor || s.meeting?.title || "",
      reason: "Needs endorsement",
      to: `/bid-committee/award-sheets/${s.id}`,
    })),
  ];

  const nextMeeting = scheduled[0];
  const kpis: Kpi[] = [
    {
      label: "Waiting on you",
      value: String(pendingItems.length),
      hint: pendingItems.length === 0 ? "You're all caught up" : "Approvals and signatures to give",
      to: PENDING_APPROVALS_PATH,
      icon: IconInbox,
      color: pendingItems.length > 0 ? "orange" : "teal",
    },
    {
      label: "Upcoming meetings",
      value: String(scheduled.length),
      hint: nextMeeting ? `Next ${dayjs(nextMeeting.plannedStart).format("ddd D MMM, HH:mm")}` : "None scheduled",
      to: "/bid-committee/meetings",
      icon: IconCalendarEvent,
      color: "cyan",
    },
    {
      label: "In progress",
      value: String(inProgress.length),
      hint: inProgress[0] ? inProgress[0].title : "No meeting is running",
      to: inProgress.length === 1 ? `/bid-committee/meetings/${inProgress[0].id}` : "/bid-committee/meetings",
      icon: IconPlayerPlay,
      color: "blue",
    },
    {
      label: "Award sheets in signing",
      value: String(sheetsInSigning),
      hint: `${sheetsApproved} approved · ${sheetsInDraft} in draft`,
      to: "/bid-committee/award-sheets",
      icon: IconFileText,
      color: "indigo",
    },
  ];

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
        <Stack gap={4}>
          <Text size="sm" c="dimmed">
            {dayjs().format("dddd, D MMMM YYYY")}
          </Text>
          <Title order={1} size="h2">
            Bid Committee
          </Title>
        </Stack>
        <Group gap="sm">
          <Tooltip label="Refresh">
            <ActionIcon variant="default" size="lg" aria-label="Refresh" onClick={refetchAll} loading={isFetching}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button variant="default" component={Link} to="/bid-committee/award-sheets" leftSection={<IconFileText size={16} />}>
            Award sheets
          </Button>
          <RoleGuard allowedRoles={["PROCUREMENT_OFFICER", "ADMIN"]}>
            <Button component={Link} to="/bid-committee/meetings/new" leftSection={<IconPlus size={16} />}>
              New meeting
            </Button>
          </RoleGuard>
        </Group>
      </Group>

      {meetingsQuery.isLoading && <BidDashboardSkeleton />}

      {meetingsQuery.isError && !meetingsQuery.data && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Couldn't load bid committee meetings">
          <Group gap="sm">
            <Text size="sm">Check your connection and try again.</Text>
            <Button size="xs" variant="light" color="red" onClick={refetchAll}>
              Retry
            </Button>
          </Group>
        </Alert>
      )}

      {meetingsQuery.data && (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            {kpis.map((kpi) => (
              <KpiTile key={kpi.label} kpi={kpi} />
            ))}
          </SimpleGrid>

          <Grid gutter="lg">
            {/* Upcoming meetings */}
            <Grid.Col span={{ base: 12, lg: 7 }}>
              <SectionCard title="Upcoming meetings" action={{ label: "Calendar", to: "/calendar" }}>
                {upcoming.length === 0 ? (
                  <EmptyState icon={IconCalendarEvent} message="No meetings are scheduled." />
                ) : (
                  <Stack gap="sm">
                    {upcoming.slice(0, 5).map((m) => {
                      const start = dayjs(m.plannedStart);
                      const live = m.status === "IN_PROGRESS";
                      const tileColor = live ? "blue" : "cyan";
                      return (
                        <Group
                          key={m.id}
                          renderRoot={(props) => <Link to={`/bid-committee/meetings/${m.id}`} {...props} />}
                          wrap="nowrap"
                          gap="md"
                          p="xs"
                          className="rounded-md no-underline transition-colors duration-150 hover:bg-[var(--mantine-color-default-hover)]"
                          style={{ color: "inherit" }}
                        >
                          <Stack
                            gap={0}
                            align="center"
                            justify="center"
                            w={52}
                            h={52}
                            style={{
                              flexShrink: 0,
                              borderRadius: 8,
                              background: `var(--mantine-color-${tileColor}-light)`,
                              color: `var(--mantine-color-${tileColor}-light-color)`,
                            }}
                          >
                            <Text size="xs" fw={600} tt="uppercase" lh={1.2}>
                              {start.format("MMM")}
                            </Text>
                            <Text size="lg" fw={700} lh={1.1} style={tabular}>
                              {start.format("D")}
                            </Text>
                          </Stack>
                          <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                            <Text size="sm" fw={600} lineClamp={1} style={dhivehiDisplayStyle(m.title)}>
                              {m.title}
                            </Text>
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {m.meetingNumber} · {start.format("ddd HH:mm")} · {m.location} ·{" "}
                              {plural(m.agendaItems?.length ?? 0, "agenda item")}
                            </Text>
                          </Stack>
                          <StatusBadge status={m.status} />
                        </Group>
                      );
                    })}
                  </Stack>
                )}
              </SectionCard>
            </Grid.Col>

            {/* Waiting on you */}
            <Grid.Col span={{ base: 12, lg: 5 }}>
              <SectionCard title="Waiting on you" action={{ label: "All approvals", to: PENDING_APPROVALS_PATH }}>
                {pendingItems.length === 0 ? (
                  <EmptyState icon={IconCircleCheck} message="Nothing is waiting on you right now." />
                ) : (
                  <Stack gap="xs">
                    {pendingItems.slice(0, 6).map((item) => (
                      <Card
                        key={item.key}
                        component={Link}
                        to={item.to}
                        withBorder
                        radius="sm"
                        padding="sm"
                        className="transition-colors duration-150 hover:bg-[var(--mantine-color-default-hover)]"
                      >
                        <Group gap="sm" wrap="nowrap">
                          <ThemeIcon variant="light" color="orange" radius="sm">
                            <IconClockHour4 size={16} aria-hidden />
                          </ThemeIcon>
                          <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                            <Text size="xs" c="dimmed">
                              {item.kind} · {item.reference}
                            </Text>
                            <Text size="sm" fw={500} lineClamp={1} style={dhivehiDisplayStyle(item.title)}>
                              {item.title || item.reference}
                            </Text>
                          </Stack>
                          <Badge variant="light" color="orange" radius="sm" size="sm" style={{ flexShrink: 0 }}>
                            {item.reason}
                          </Badge>
                        </Group>
                      </Card>
                    ))}
                    {pendingItems.length > 6 && (
                      <Anchor component={Link} to={PENDING_APPROVALS_PATH} size="sm" ta="center">
                        {plural(pendingItems.length - 6, "more item")}
                      </Anchor>
                    )}
                  </Stack>
                )}
              </SectionCard>
            </Grid.Col>

            {/* Latest meetings */}
            <Grid.Col span={12}>
              <SectionCard title="Latest meetings" action={{ label: "All meetings", to: "/bid-committee/meetings" }}>
                {latestMeetings.length === 0 ? (
                  <EmptyState icon={IconCalendarEvent} message="No meetings yet." />
                ) : (
                  <Table.ScrollContainer minWidth={720}>
                    <Table highlightOnHover verticalSpacing="sm">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Meeting</Table.Th>
                          <Table.Th>Date</Table.Th>
                          <Table.Th>Location</Table.Th>
                          <Table.Th ta="right">Agenda</Table.Th>
                          <Table.Th ta="right">Present</Table.Th>
                          <Table.Th ta="right">Award sheets</Table.Th>
                          <Table.Th>Status</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {latestMeetings.map((m) => {
                          const attendances = m.attendances ?? [];
                          const present = attendances.filter((a) => a.presence && !a.isSecretary).length;
                          const members = attendances.filter((a) => !a.isSecretary).length;
                          return (
                            <Table.Tr key={m.id}>
                              <Table.Td maw={360}>
                                <Anchor component={Link} to={`/bid-committee/meetings/${m.id}`} size="sm" fw={600}>
                                  {m.meetingNumber}
                                </Anchor>
                                <Text size="xs" c="dimmed" lineClamp={1} style={dhivehiDisplayStyle(m.title)}>
                                  {m.title}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" style={tabular}>
                                  {dayjs(m.plannedStart).format("D MMM YYYY")}
                                </Text>
                                <Text size="xs" c="dimmed" style={tabular}>
                                  {dayjs(m.plannedStart).format("HH:mm")}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" lineClamp={1}>
                                  {m.location}
                                </Text>
                              </Table.Td>
                              <Table.Td ta="right" style={tabular}>
                                {m.agendaItems?.length ?? 0}
                              </Table.Td>
                              <Table.Td ta="right" style={tabular}>
                                {members > 0 ? `${present}/${members}` : "—"}
                              </Table.Td>
                              <Table.Td ta="right" style={tabular}>
                                {awardSheetCountByMeeting.get(m.id) ?? 0}
                              </Table.Td>
                              <Table.Td>
                                <StatusBadge status={m.status} />
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                )}
              </SectionCard>
            </Grid.Col>
          </Grid>
        </>
      )}
    </Stack>
  );
};
